import { call } from '@advanza/api'
import { Divider, Icon } from '@advanza/ui'
import LoadingDots from '@trustoo/components/ui/LoadingDots'
import HybridTooltip from '@trustoo/components/ui/tooltip/HybridTooltip'
import Card from 'components/Card'
import { CSSProperties, useEffect, useState } from 'react'

interface Props {
    providerId: number
}

interface HiredPercentagePeriodStats {
    lastMonth: number
    last3Months: number
    allTime: number
}
interface HiredPercentageStatsData {
    allServices: HiredPercentagePeriodStats
    mainService: HiredPercentagePeriodStats
    serviceWide: HiredPercentagePeriodStats
    mainServiceName: string
}

const PERIOD_ROWS = [
    { label: 'Last Month', period: 'lastMonth' },
    { label: 'Last 3 Months', period: 'last3Months' },
    { label: 'All Time', period: 'allTime' },
] as const

const headerCellStyle: CSSProperties = {
    padding: '10px',
    borderBottom: '2px solid #ddd',
    fontWeight: 'bold',
}

interface ColumnHeaderTooltipProps {
    label: string
    numerator: string
    denominator: string
}

const ColumnHeaderTooltip = ({ label, numerator, denominator }: ColumnHeaderTooltipProps) => (
    <div className="flex items-center gap-1">
        {label}
        <HybridTooltip
            childrenContent={
                <div className="flex flex-col gap-1 text-center text-sm">
                    <span>{numerator}</span>
                    <span className="border-t-2 border-neutral-500" aria-hidden="true" />
                    <span>{denominator}</span>
                </div>
            }>
            <span>
                <Icon name="info" fontSize={14} className="cursor-help text-neutral-500" />
            </span>
        </HybridTooltip>
    </div>
)

const HiredPercentageStats = ({ providerId }: Props) => {
    const [isFetching, setIsFetching] = useState(false)
    const [data, setData] = useState<HiredPercentageStatsData | null>(null)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!providerId) {
            return
        }

        setIsFetching(true)
        setError(null)

        call(`office/providers/get-hired-percentage-stats/${providerId}`)
            .then((response: HiredPercentageStatsData) => {
                setData(response)
                setIsFetching(false)
            })
            .catch((err) => {
                setError(err.message || 'Failed to load hired percentage statistics')
                setIsFetching(false)
            })
    }, [providerId])

    if (isFetching) {
        return (
            <Card header="Hired %" icon="check_circle" useToggle>
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <LoadingDots />
                </div>
            </Card>
        )
    }

    if (error) {
        return (
            <Card header="Hired %" icon="check_circle" useToggle>
                <div style={{ padding: '20px', color: '#d32f2f' }}>
                    <strong>Error:</strong> {error}
                </div>
            </Card>
        )
    }

    if (!data) {
        return null
    }

    const { allServices, mainService, serviceWide, mainServiceName } = data
    const serviceLabel = mainServiceName || 'Main Service'

    const formatPercentage = (value: number | undefined) => {
        return typeof value === 'number' ? Math.round(value) : value
    }

    const customHeader = (
        <span>
            Hired{' '}
            <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
                {formatPercentage(allServices.allTime)}%
            </span>{' '}
            (all time)
        </span>
    )

    return (
        <Card header={customHeader} icon="check_circle" useToggle>
            <Divider />
            <div style={{ padding: '16px' }}>
                <table
                    style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '13px',
                    }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                            <th style={{ ...headerCellStyle, textAlign: 'left' }}>Timeframe</th>
                            <th style={{ ...headerCellStyle, textAlign: 'right' }}>
                                <ColumnHeaderTooltip
                                    label="All services"
                                    numerator="this provider's hired matches"
                                    denominator="this provider's confirmed matches"
                                />
                            </th>
                            <th style={{ ...headerCellStyle, textAlign: 'right' }}>
                                <ColumnHeaderTooltip
                                    label={`% own ${serviceLabel}`}
                                    numerator={`this provider's hired matches on ${serviceLabel}`}
                                    denominator={`this provider's confirmed matches on ${serviceLabel}`}
                                />
                            </th>
                            <th style={{ ...headerCellStyle, textAlign: 'right' }}>
                                <ColumnHeaderTooltip
                                    label={`% ${serviceLabel}`}
                                    numerator={`all hired matches on ${serviceLabel}`}
                                    denominator={`all confirmed matches on ${serviceLabel}`}
                                />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {PERIOD_ROWS.map(({ label, period }, index) => (
                            <tr
                                key={period}
                                style={
                                    index < PERIOD_ROWS.length - 1
                                        ? { borderBottom: '1px solid #eee' }
                                        : undefined
                                }>
                                <td style={{ padding: '10px' }}>{label}</td>
                                <td
                                    style={{
                                        padding: '10px',
                                        textAlign: 'right',
                                        fontWeight: 'bold',
                                    }}>
                                    {formatPercentage(allServices[period])}%
                                </td>
                                <td style={{ padding: '10px', textAlign: 'right', color: '#666' }}>
                                    {formatPercentage(mainService[period])}%
                                </td>
                                <td style={{ padding: '10px', textAlign: 'right', color: '#666' }}>
                                    {formatPercentage(serviceWide?.[period])}%
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    )
}

export default HiredPercentageStats
